import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { useIsLoggedIn, useTranslate } from "@/hooks";
import { getBalance, redeemPromoCode } from "@/services";
import { dispatch, updateBalance } from "@/store";
import { clearStoredPromoCode, getStoredPromoCode } from "@/utils/attribution";

// Same queries PromoCodeBox refreshes after a code is accepted.
const GRANT_QUERY_KEYS = [["promotion-grants"], ["promotion-free-spins"]];

/**
 * Redeems a code from a ?promo= link or the sign-up form once the player is signed in
 * (right after sign-up, or at the next login), then forgets it either way.
 */
export const usePromoCodeRedemption = () => {
  const isLoggedIn = useIsLoggedIn();
  const t = useTranslate();
  const queryClient = useQueryClient();
  // StrictMode runs effects twice; one request per code.
  const pending = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) return;
    const code = getStoredPromoCode();
    if (!code || pending.current === code) return;
    pending.current = code;
    clearStoredPromoCode();

    redeemPromoCode(code)
      .then(async (result) => {
        toast.success(t("promo-code.success", { name: result.campaignName || code }), {
          hideProgressBar: true,
        });
        await Promise.all(
          GRANT_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
        );
        dispatch(updateBalance(await getBalance()));
      })
      .catch((error: Error) => toast.error(error?.message || t("promo-code.failed")))
      .finally(() => {
        pending.current = null;
      });
  }, [isLoggedIn, t, queryClient]);
};
